import { useEffect, useRef, useState } from "react";
import { askAssistant, assistantHistory, clearAssistant } from "../api.js";
import type { ChatMessage } from "../api.js";
import type { Session } from "../app.js";

const EXAMPLES = [
  "What did I upload this week?",
  "Find the screenshot about billing",
  "Make design-notes return the raw file",
  "Rename my latest upload to demo",
];

/** A per-account agent that can search, rename and delete the files it owns. */
export function Assistant({ session }: { session: NonNullable<Session> }) {
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [draft, setDraft] = useState("");
  const [thinking, setThinking] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const bottom = useRef<HTMLDivElement>(null);
  const input = useRef<HTMLInputElement>(null);

  useEffect(() => {
    void assistantHistory().then(setMessages);
    input.current?.focus();
  }, []);

  useEffect(() => {
    bottom.current?.scrollIntoView({ behavior: "smooth", block: "end" });
  }, [messages.length, thinking]);

  const send = async (text: string) => {
    if (!text.trim() || thinking) return;
    setDraft("");
    setError(null);
    setThinking(true);
    setMessages((current) => [...current, { role: "user", content: text, at: Date.now() }]);
    try {
      setMessages(await askAssistant(text));
    } catch {
      setError("That did not go through. Try again.");
    } finally {
      setThinking(false);
      input.current?.focus();
    }
  };

  return (
    <main className="stack narrow">
      <div className="section-head">
        <h1>Assistant</h1>
        <span className="meta mono">
          acts only on {session.user.username}&rsquo;s files
        </span>
      </div>

      <div className="chat" aria-live="polite">
        {messages.length === 0 && !thinking ? (
          <div className="chat__empty">
            <p className="meta">
              It can search, rename, re-mode and delete your files. Try one:
            </p>
            <ul className="chat__examples">
              {EXAMPLES.map((example) => (
                <li key={example}>
                  <button className="button" type="button" onClick={() => void send(example)}>
                    {example}
                  </button>
                </li>
              ))}
            </ul>
          </div>
        ) : (
          messages.map((message) => (
            <div
              key={`${message.at}-${message.role}`}
              className={`chat__msg chat__msg--${message.role}`}
            >
              <span className="chat__who mono">
                {message.role === "user" ? "you" : "saucer"}
              </span>
              <p className="chat__line">{message.content}</p>
            </div>
          ))
        )}
        {thinking ? (
          <div className="chat__msg chat__msg--assistant">
            <span className="chat__who mono">saucer</span>
            <p className="chat__line chat__line--thinking">
              <span className="chat__dot" />
              <span className="chat__dot" />
              <span className="chat__dot" />
            </p>
          </div>
        ) : null}
        <div ref={bottom} />
      </div>

      {error ? <p className="notice notice--error">{error}</p> : null}

      <form
        className="row"
        onSubmit={(event) => {
          event.preventDefault();
          void send(draft);
        }}
      >
        <input
          ref={input}
          className="input"
          value={draft}
          onChange={(event) => setDraft(event.target.value)}
          placeholder="Ask about your files"
          disabled={thinking}
        />
        <button className="button button--primary" type="submit" disabled={thinking || !draft.trim()}>
          Send
        </button>
        {messages.length > 0 ? (
          <button
            className="button button--quiet"
            type="button"
            onClick={async () => {
              await clearAssistant();
              setMessages([]);
              input.current?.focus();
            }}
          >
            Clear
          </button>
        ) : null}
      </form>
    </main>
  );
}
